import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useEffect, useRef } from "react";
import { retainThreadDetailSubscription } from "../../environments/runtime/service";
import { sanitizeThreadErrorMessage } from "../../rpc/transportError";
import { toastManager } from "../ui/toast";
import { type Thread } from "../../types";

export function useServerThreadSubscription(input: {
  readonly routeKind: "server" | "draft";
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
}): void {
  useEffect(() => {
    if (input.routeKind !== "server") {
      return;
    }
    return retainThreadDetailSubscription(input.environmentId, input.threadId);
  }, [input.environmentId, input.routeKind, input.threadId]);
}

export function useMarkSettledThreadVisited(input: {
  readonly serverThread: Thread | undefined;
  readonly activeLatestTurn: Thread["latestTurn"] | null;
  readonly latestTurnSettled: boolean;
  readonly activeThreadLastVisitedAt: string | undefined;
  readonly markThreadVisited: (threadKey: string) => void;
}): void {
  const {
    activeLatestTurn,
    activeThreadLastVisitedAt,
    latestTurnSettled,
    markThreadVisited,
    serverThread,
  } = input;
  useEffect(() => {
    if (!serverThread?.id) return;
    if (!latestTurnSettled) return;
    if (!activeLatestTurn?.completedAt) return;
    const turnCompletedAt = Date.parse(activeLatestTurn.completedAt);
    if (Number.isNaN(turnCompletedAt)) return;
    const lastVisitedAt = activeThreadLastVisitedAt ? Date.parse(activeThreadLastVisitedAt) : NaN;
    if (!Number.isNaN(lastVisitedAt) && lastVisitedAt >= turnCompletedAt) return;

    markThreadVisited(scopedThreadKey(scopeThreadRef(serverThread.environmentId, serverThread.id)));
  }, [
    activeLatestTurn?.completedAt,
    activeThreadLastVisitedAt,
    latestTurnSettled,
    markThreadVisited,
    serverThread?.environmentId,
    serverThread?.id,
  ]);
}

export function useApexDisconnectToast(input: {
  readonly activeThread: Thread | undefined;
  readonly activeLatestTurn: Thread["latestTurn"] | null;
  readonly latestTurnSettled: boolean;
}): void {
  const apexDisconnectToastedTurnIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!input.latestTurnSettled) return;
    const turnId = input.activeLatestTurn?.turnId;
    if (!turnId) return;
    if (apexDisconnectToastedTurnIdRef.current === turnId) return;
    const provider = input.activeThread?.session?.provider;
    if (provider !== "apex" && provider !== "cmmd") return;
    if (input.activeThread?.session?.status !== "error") return;
    const lastError = sanitizeThreadErrorMessage(input.activeThread?.session?.lastError ?? null);
    if (!lastError?.includes("disconnected")) return;
    apexDisconnectToastedTurnIdRef.current = turnId;
    toastManager.add({
      type: "warning",
      title: "CMMD disconnected",
      description: "The CMMD connection was lost. Retry your message.",
    });
  }, [
    input.activeLatestTurn?.turnId,
    input.activeThread?.session?.lastError,
    input.activeThread?.session?.provider,
    input.activeThread?.session?.status,
    input.latestTurnSettled,
  ]);
}
